import {z} from 'zod';

import {BaseVoidRequestSchema, BaseVoidResponseSchema} from '@dms/api/shared';

const ServiceOrderDocumentSchema = z.object({
  templateId: z.string().nullable().optional(),
  docSeriesId: z.string().nullable().optional(),
});

const ServiceOrderDocSeriesSchema = ServiceOrderDocumentSchema.pick({docSeriesId: true});

const PaymentTypeSchema = z.object({
  isAllowed: z.boolean(),
  isAutoInitiatePayment: z.boolean().nullish(),
  cashRegisterId: z.string().nullish(),
  taxDocument: ServiceOrderDocSeriesSchema.nullish(),
  taxDocumentCorrection: ServiceOrderDocSeriesSchema.nullish(),
  deposit: ServiceOrderDocSeriesSchema.nullish(),
  invoice: ServiceOrderDocumentSchema.nullable().optional(),
  invoiceCorrection: ServiceOrderDocumentSchema.nullable().optional(),
  invoiceEu: ServiceOrderDocumentSchema.nullable().optional(),
  invoiceEuCorrection: ServiceOrderDocumentSchema.nullable().optional(),
  invoiceOutsideEu: ServiceOrderDocumentSchema.nullable().optional(),
  invoiceOutsideEuCorrection: ServiceOrderDocumentSchema.nullable().optional(),
});

const DocumentsSchema = z.object({
  orderSheet: ServiceOrderDocumentSchema.nullable().optional(),
  orderOffer: ServiceOrderDocumentSchema.nullable().optional(),
  orderClaim: ServiceOrderDocumentSchema.nullable().optional(),
  orderHandlingProtocol: z
    .object({
      templateId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  orderResolutionProtocol: z
    .object({
      templateId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  serviceOrderIssue: ServiceOrderDocumentSchema.nullable().optional(),
  serviceOrderReturn: ServiceOrderDocumentSchema.nullable().optional(),
  fastTrackIssue: z.object({isEnable: z.boolean()}).merge(ServiceOrderDocumentSchema),
  order: z
    .object({
      isUniqueDocSeries: z.boolean(),
      docSeriesId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

const PaymentTypeOptionsSchema = z.object({
  defaultPaymentType: z
    .enum(['BANK_TRANSFER', 'CARD', 'CASH', 'CASH_ON_DELIVERY', 'INTERNAL'])
    .nullable(),
  cashPayment: PaymentTypeSchema.nullable(),
  cashOnDeliveryPayment: PaymentTypeSchema.nullable(),
  cardPayment: PaymentTypeSchema.nullable(),
  bankPayment: PaymentTypeSchema.nullable(),
  internalPayment: PaymentTypeSchema.nullable(),
});

export const PostServiceOrderVariantApiResponseSchema = z
  .object({
    serviceOrderVariantId: z.string().nullable(),
  })
  .nullable();

export type PostServiceOrderVariantApiResponse = z.infer<
  typeof PostServiceOrderVariantApiResponseSchema
>;

export const PostServiceOrderVariantApiArgSchema = z.object({
  body: z
    .object({
      serviceCaseTypeId: z.string(),
      isDefaultForServiceCaseType: z.boolean(),
      serviceOrderTypeId: z.string(),
      name: z.string(),
      note: z.string().nullable().optional(),
      priceType: z.string(),
      priceTypeRatio: z.number().nullable().optional(),
      rateType: z.string(),
      rateTypeRatio: z.number().nullable().optional(),
      isUnitPriceWithVat: z.boolean(),
      defaultCustomerId: z.string().nullable().optional(),
      isChangeableDefaultCustomer: z.boolean(),
      suffix: z.string(),
      minimalMaterialMargin: z.number().nullable().optional(),
      minimalWorkMargin: z.number().nullable().optional(),
      isActive: z.boolean(),
      isCancelReasonRequired: z.boolean(),
    })
    .nullable(),
});

export type PostServiceOrderVariantApiArg = z.infer<typeof PostServiceOrderVariantApiArgSchema>;

export const GetServiceOrderVariantApiResponseSchema = z
  .object({
    id: z.string(),
    authorizationProfileId: z.string(),
    serviceCaseTypeId: z.string(),
    serviceOrderTypeId: z.string(),
    name: z.string(),
    note: z.string().nullable().optional(),
    priceType: z.string(),
    priceTypeRatio: z.number().nullable().optional(),
    rateType: z.string(),
    rateTypeRatio: z.number().nullable().optional(),
    isUnitPriceWithVat: z.boolean(),
    defaultCustomerId: z.string().nullable().optional(),
    isChangeableDefaultCustomer: z.boolean(),
    suffix: z.string().nullable().optional(),
    minimalMaterialMargin: z.number().nullable().optional(),
    minimalWorkMargin: z.number().nullable().optional(),
    isDefaultForServiceCaseType: z.boolean(),
    isActive: z.boolean(),
    isCancelReasonRequired: z.boolean(),
  })
  .nullable();

export type GetServiceOrderVariantApiResponse = z.infer<
  typeof GetServiceOrderVariantApiResponseSchema
>;

export const GetServiceOrderVariantApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantApiArg = z.infer<typeof GetServiceOrderVariantApiArgSchema>;

export const PostServiceOrderVariantSetAsActiveApiResponseSchema = BaseVoidResponseSchema;

export type PostServiceOrderVariantSetAsActiveApiResponse = z.infer<
  typeof PostServiceOrderVariantSetAsActiveApiResponseSchema
>;

export const PostServiceOrderVariantSetAsActiveApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type PostServiceOrderVariantSetAsActiveApiArg = z.infer<
  typeof PostServiceOrderVariantSetAsActiveApiArgSchema
>;

export const PostServiceOrderVariantSetAsInactiveResponseSchema = BaseVoidResponseSchema;

export type PostServiceOrderVariantSetAsInactiveResponse = z.infer<
  typeof PostServiceOrderVariantSetAsInactiveResponseSchema
>;

export const PostServiceOrderVariantSetAsInactiveApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type PostServiceOrderVariantSetAsInactiveApiArg = z.infer<
  typeof PostServiceOrderVariantSetAsInactiveApiArgSchema
>;

export const PatchServiceOrderVariantApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantApiResponse = z.infer<
  typeof PatchServiceOrderVariantApiResponseSchema
>;

export const DeleteServiceOrderVariantApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type DeleteServiceOrderVariantApiArg = z.infer<typeof DeleteServiceOrderVariantApiArgSchema>;

export const DeleteServiceOrderVariantResponseSchema = BaseVoidResponseSchema;

export type DeleteServiceOrderVariantResponse = z.infer<
  typeof DeleteServiceOrderVariantResponseSchema
>;

export const PatchServiceOrderVariantApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: z
    .object({
      serviceCaseTypeId: z.string(),
      isDefaultForServiceCaseType: z.boolean(),
      serviceOrderTypeId: z.string(),
      name: z.string(),
      note: z.string().nullable().optional(),
      priceType: z.string(),
      priceTypeRatio: z.number().nullable().optional(),
      rateType: z.string(),
      rateTypeRatio: z.number().nullable().optional(),
      isUnitPriceWithVat: z.boolean(),
      defaultCustomerId: z.string().nullable().optional(),
      isChangeableDefaultCustomer: z.boolean(),
      suffix: z.string(),
      minimalMaterialMargin: z.number().nullable().optional(),
      minimalWorkMargin: z.number().nullable().optional(),
      isActive: z.boolean(),
      isCancelReasonRequired: z.boolean(),
    })
    .nullable(),
});

export type PatchServiceOrderVariantApiArg = z.infer<typeof PatchServiceOrderVariantApiArgSchema>;

export const GetServiceOrderVariantDocumentsApiResponseSchema = DocumentsSchema.nullable();

export type GetServiceOrderVariantDocumentsApiResponse = z.infer<
  typeof GetServiceOrderVariantDocumentsApiResponseSchema
>;

export const GetServiceOrderVariantDocumentsApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantDocumentsApiArg = z.infer<
  typeof GetServiceOrderVariantDocumentsApiArgSchema
>;

export const PatchServiceOrderVariantDocumentsApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantDocumentsApiResponse = z.infer<
  typeof PatchServiceOrderVariantDocumentsApiResponseSchema
>;

export const PatchServiceOrderVariantDocumentsApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: DocumentsSchema.nullable(),
});
export type PatchServiceOrderVariantDocumentsApiArg = z.infer<
  typeof PatchServiceOrderVariantDocumentsApiArgSchema
>;

export const GetServiceOrderVariantMandatoryFieldsApiResponseSchema = z
  .array(
    z.object({
      serviceOrderTypeId: z.string().nullable().optional(),
      field: z.string().nullable().optional(),
      group: z.number().nullable().optional(),
      position: z.number().nullable().optional(),
      isSelected: z.boolean().nullable().optional(),
      mandatoryCondition: z
        .array(
          z.object({
            event: z.string(),
            isSelected: z.boolean(),
          })
        )
        .nullable()
        .optional(),
    })
  )
  .nullable()
  .optional();

export type GetServiceOrderVariantMandatoryFieldsApiResponse = z.infer<
  typeof GetServiceOrderVariantMandatoryFieldsApiResponseSchema
>;

export const GetServiceOrderVariantMandatoryFieldsApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantMandatoryFieldsApiArg = z.infer<
  typeof GetServiceOrderVariantMandatoryFieldsApiArgSchema
>;

export const PatchServiceOrderVariantMandatoryFieldsApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantMandatoryFieldsApiResponse = z.infer<
  typeof PatchServiceOrderVariantMandatoryFieldsApiResponseSchema
>;

export const PatchServiceOrderVariantMandatoryFieldsApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: z.array(
    z.object({
      field: z.string(),
      isSelected: z.boolean(),
      mandatoryCondition: z
        .array(
          z.object({
            event: z.string(),
            isSelected: z.boolean(),
          })
        )
        .nullable()
        .optional(),
    })
  ),
});

export type PatchServiceOrderVariantMandatoryFieldsApiArg = z.infer<
  typeof PatchServiceOrderVariantMandatoryFieldsApiArgSchema
>;

export const GetSettingsServiceOrderVariantsApiResponseSchema = z.array(
  z
    .object({
      id: z.string(),
      isActive: z.boolean(),
      authorizationProfileId: z.string(),
      serviceCaseTypeId: z.string(),
      serviceOrderTypeId: z.string(),
      name: z.string(),
      note: z.string().nullish(),
      priceType: z.string(),
      priceTypeRatio: z.number(),
      rateType: z.string(),
      rateTypeRatio: z.number(),
      isUnitPriceWithVat: z.boolean(),
      defaultCustomerId: z.string().nullish(),
      isChangeableDefaultCustomer: z.boolean(),
      suffix: z.string().nullish(),
      minimalMaterialMargin: z.number().nullish(),
      minimalWorkMargin: z.number().nullish(),
      isDefaultForServiceCaseType: z.boolean(),
      isCancelReasonRequired: z.boolean(),
    })
    .nullable()
);

export type GetSettingsServiceOrderVariantsApiResponse = z.infer<
  typeof GetSettingsServiceOrderVariantsApiResponseSchema
>;

export const GetSettingsServiceOrderVariantsApiArgSchema = BaseVoidRequestSchema;

export type GetSettingsServiceOrderVariantsApiArg = z.infer<
  typeof GetSettingsServiceOrderVariantsApiArgSchema
>;

export const GetSettingsServiceOrderVariantsAllApiArgSchema = z
  .object({
    // Overrides the global branch header, for example for a rule that targets another branch.
    branchId: z.string().optional(),
  })
  .optional();

export type GetSettingsServiceOrderVariantsAllApiArg = z.infer<
  typeof GetSettingsServiceOrderVariantsAllApiArgSchema
>;

export const GetServiceOrderVariantPaymentTypeApiResponseSchema =
  PaymentTypeOptionsSchema.nullable();

export type GetServiceOrderVariantPaymentTypeApiResponse = z.infer<
  typeof GetServiceOrderVariantPaymentTypeApiResponseSchema
>;

export const GetServiceOrderVariantPaymentTypeApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantPaymentTypeApiArg = z.infer<
  typeof GetServiceOrderVariantPaymentTypeApiArgSchema
>;

export const PatchServiceOrderVariantPaymentTypeApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantPaymentTypeApiApiResponse = z.infer<
  typeof PatchServiceOrderVariantPaymentTypeApiResponseSchema
>;

export const PatchServiceOrderVariantPaymentTypeApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: z
    .object({
      defaultPaymentType: z
        .enum(['BANK_TRANSFER', 'CARD', 'CASH', 'CASH_ON_DELIVERY', 'INTERNAL'])
        .nullish(),
      cardPayment: PaymentTypeSchema.nullable(),
      cashPayment: PaymentTypeSchema.nullable(),
      cashOnDeliveryPayment: PaymentTypeSchema.nullable(),
      bankPayment: PaymentTypeSchema.nullable(),
      internalPayment: PaymentTypeSchema.nullable(),
    })
    .nullable(),
});

export type PatchServiceOrderVariantPaymentTypeApiApiArg = z.infer<
  typeof PatchServiceOrderVariantPaymentTypeApiArgSchema
>;

const DepositPaymentMethodSchema = z.object({
  isAllowed: z.boolean(),
  taxDocument: ServiceOrderDocumentSchema.nullish(),
  taxDocumentEu: ServiceOrderDocSeriesSchema.nullish(),
  taxDocumentOutsideEu: ServiceOrderDocSeriesSchema.nullish(),
  taxDocumentCorrection: ServiceOrderDocSeriesSchema.nullish(),
  taxDocumentCorrectionEu: ServiceOrderDocSeriesSchema.nullish(),
  taxDocumentCorrectionOutsideEu: ServiceOrderDocSeriesSchema.nullish(),
});

const DepositSchema = z.object({
  allowDeposit: z.boolean(),
  templateId: z.string().nullish(),
  documentSerieId: z.string().nullish(),
  defaultPaymentType: z.enum(['BANK_TRANSFER', 'CARD', 'CASH']).nullish(),
  cashPayment: DepositPaymentMethodSchema.nullable(),
  cardPayment: DepositPaymentMethodSchema.nullable(),
  bankPayment: DepositPaymentMethodSchema.nullable(),
});

export const GetServiceOrderVariantDepositApiResponseSchema = DepositSchema.nullable();

export type GetServiceOrderVariantDepositApiResponse = z.infer<
  typeof GetServiceOrderVariantDepositApiResponseSchema
>;

export const GetServiceOrderVariantDepositApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantDepositApiArg = z.infer<
  typeof GetServiceOrderVariantDepositApiArgSchema
>;

export const PatchServiceOrderVariantDepositApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantDepositApiResponse = z.infer<
  typeof PatchServiceOrderVariantDepositApiResponseSchema
>;

export const PatchServiceOrderVariantDepositApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: DepositSchema.nullable(),
});

export type PatchServiceOrderVariantDepositApiArg = z.infer<
  typeof PatchServiceOrderVariantDepositApiArgSchema
>;

export const GetServiceOrderVariantAccountingApiResponseSchema = z.object({
  isTaxDeductible: z.boolean(),
  fixedVatType: z.string().nullable().optional(),
  excludedServiceCenterIds: z.array(z.string().optional()).optional(),
  defaultServiceCenterId: z.string().nullable().optional(),
  isDefaultServiceCenterLocked: z.boolean(),
});

export type GetServiceOrderVariantAccountingApiResponse = z.infer<
  typeof GetServiceOrderVariantAccountingApiResponseSchema
>;

export const GetServiceOrderVariantAccountingApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantAccountingApiArg = z.infer<
  typeof GetServiceOrderVariantAccountingApiArgSchema
>;

export const PatchServiceOrderVariantAccountingApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantAccountingApiResponse = z.infer<
  typeof PatchServiceOrderVariantAccountingApiResponseSchema
>;

export const PatchServiceOrderVariantAccountingApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: z
    .object({
      isTaxDeductible: z.boolean(),
      fixedVatType: z.string().nullable().optional(),
      excludedServiceCenterIds: z.array(z.string().nullable()).optional(),
      defaultServiceCenterId: z.string().nullable().optional(),
      isDefaultServiceCenterLocked: z.boolean(),
    })
    .nullable(),
});

export type PatchServiceOrderVariantAccountingApiArg = z.infer<
  typeof PatchServiceOrderVariantAccountingApiArgSchema
>;
export const PostServiceOrderVariantSetAsDefaultApiResponseSchema = BaseVoidResponseSchema;

export type PostServiceOrderVariantSetAsDefaultApiResponse = z.infer<
  typeof PostServiceOrderVariantSetAsDefaultApiResponseSchema
>;

export const PostServiceOrderVariantSetAsDefaultApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type PostServiceOrderVariantSetAsDefaultApiArg = z.infer<
  typeof PostServiceOrderVariantSetAsDefaultApiArgSchema
>;

export const GetServiceOrderVariantReportingApiResponseSchema = z.object({
  excludedServiceOrderGroupIds: z.array(z.string()).nullable(),
  defaultServiceOrderGroupId: z.string().nullable(),
  isDefaultServiceOrderGroupLocked: z.boolean(),
  isPreDeliveryInspection: z.boolean(),
  isDbiReportBodyshop: z.boolean().nullable(),
});

export type GetServiceOrderVariantReportingApiResponse = z.infer<
  typeof GetServiceOrderVariantReportingApiResponseSchema
>;

export const GetServiceOrderVariantReportingApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantReportingApiArg = z.infer<
  typeof GetServiceOrderVariantReportingApiArgSchema
>;

export const PatchServiceOrderVariantReportingApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantReportingApiResponse = z.infer<
  typeof PatchServiceOrderVariantReportingApiResponseSchema
>;

export const PatchServiceOrderVariantReportingApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: z.object({
    excludedServiceOrderGroupIds: z.array(z.string()).nullable(),
    defaultServiceOrderGroupId: z.string().nullish(),
    isDefaultServiceOrderGroupLocked: z.boolean(),
    isPreDeliveryInspection: z.boolean(),
    isDbiReportBodyshop: z.boolean(),
  }),
});

export type PatchServiceOrderVariantReportingApiArg = z.infer<
  typeof PatchServiceOrderVariantReportingApiArgSchema
>;

export const GetServiceOrderVariantAvailableDocumentsApiResponseSchema = z.array(
  z.object({
    documentCode: z.enum([
      'workshop-fast_track',
      'workshop-handling_protocol',
      'workshop-order_list_commercial',
      'workshop-order_list_contract',
      'workshop-order_list_insurance',
      'workshop-order_list_internal',
      'workshop-order_list_warranty',
      'workshop-power_of_attorney',
      'workshop-resolution_protocol',
      'workshop-service_order_offer-v2',
      'workshop-service_order_overview',
      'workshop-tire_storage_form',
      'workshop-invoice',
      'workshop-invoice_internal',
      'workshop-job_card',
      'service-order-issue',
      'service-order-parts-return',
    ]),
    title: z.string(),
    templates: z.array(
      z.object({
        id: z.string().uuid(),
        title: z.string(),
        authorizationProfileId: z.string().uuid().nullable(),
        isHidden: z.boolean(),
      })
    ),
  })
);

export type GetServiceOrderVariantAvailableDocumentsApiResponse = z.infer<
  typeof GetServiceOrderVariantAvailableDocumentsApiResponseSchema
>;

export const GetServiceOrderVariantAvailableDocumentsApiArgSchema = z.object({
  serviceOrderVariantId: z.string().uuid().optional(),
  serviceCaseTypeId: z.string().uuid().optional(),
  serviceOrderTypeId: z.string().uuid().optional(),
});

export type GetServiceOrderVariantAvailableDocumentsApiArg = z.infer<
  typeof GetServiceOrderVariantAvailableDocumentsApiArgSchema
>;

export const GetServiceOrderVariantWarehousePriorityApiResponseSchema = z.array(
  z.object({
    warehouseId: z.string(),
    warehouseName: z.string(),
    warehouseNumber: z.string(),
    warehousePriority: z.number(),
    authorizationProfile: z.object({
      id: z.string(),
      name: z.string(),
    }),
    branch: z.object({
      id: z.string(),
      name: z.string(),
    }),
  })
);

export type GetServiceOrderVariantWarehousePriorityApiResponse = z.infer<
  typeof GetServiceOrderVariantWarehousePriorityApiResponseSchema
>;

export const GetServiceOrderVariantWarehousePriorityApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantWarehousePriorityApiArg = z.infer<
  typeof GetServiceOrderVariantWarehousePriorityApiArgSchema
>;

export const PatchServiceOrderVariantWarehousePriorityApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantWarehousePriorityApiResponse = z.infer<
  typeof PatchServiceOrderVariantWarehousePriorityApiResponseSchema
>;

export const PatchServiceOrderVariantWarehousePriorityApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: z.object({
    warehouseIds: z.array(z.string()),
  }),
});

export type PatchServiceOrderVariantWarehousePriorityApiArg = z.infer<
  typeof PatchServiceOrderVariantWarehousePriorityApiArgSchema
>;

export const GetServiceOrderVariantDefaultWarehousePriorityApiResponseSchema = z.array(
  z.object({
    warehouseId: z.string(),
    warehouseName: z.string(),
    warehouseNumber: z.string(),
    warehousePriority: z.number(),
    authorizationProfile: z.object({
      id: z.string(),
      name: z.string(),
    }),
    branch: z.object({
      id: z.string(),
      name: z.string(),
    }),
  })
);

export type GetServiceOrderVariantDefaultWarehousePriorityApiResponse = z.infer<
  typeof GetServiceOrderVariantDefaultWarehousePriorityApiResponseSchema
>;

export const GetServiceOrderVariantDefaultWarehousePriorityApiArgSchema = z.object({});

export type GetServiceOrderVariantDefaultWarehousePriorityApiArg = z.infer<
  typeof GetServiceOrderVariantDefaultWarehousePriorityApiArgSchema
>;

export const GetServiceOrderVariantIntegrationApiResponseSchema = z.object({
  isDefaultForXentryServiceMeasures: z.boolean(),
  isExcludedFromXentrySynchronization: z.boolean(),
  isAutomaticSendToChronos: z.boolean(),
});

export type GetServiceOrderVariantIntegrationApiResponse = z.infer<
  typeof GetServiceOrderVariantIntegrationApiResponseSchema
>;

export const GetServiceOrderVariantIntegrationApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantIntegrationApiArg = z.infer<
  typeof GetServiceOrderVariantIntegrationApiArgSchema
>;

export const PatchServiceOrderVariantIntegrationApiResponseSchema = BaseVoidResponseSchema;

export type PatchServiceOrderVariantIntegrationApiResponse = z.infer<
  typeof PatchServiceOrderVariantIntegrationApiResponseSchema
>;

export const PatchServiceOrderVariantIntegrationApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
  body: z.object({
    isDefaultForXentryServiceMeasures: z.boolean(),
    isExcludedFromXentrySynchronization: z.boolean(),
    isAutomaticSendToChronos: z.boolean(),
  }),
});

export type PatchServiceOrderVariantIntegrationApiArg = z.infer<
  typeof PatchServiceOrderVariantIntegrationApiArgSchema
>;

export const GetServiceOrderVariantIntegrationActionsApiResponseSchema = z.object({
  actions: z.array(z.enum(['AUTO_SEND_TO_CHRONOS', 'SET_AS_DEFAULT_FOR_XENTRY'])),
});

export type GetServiceOrderVariantIntegrationActionsApiResponse = z.infer<
  typeof GetServiceOrderVariantIntegrationActionsApiResponseSchema
>;

export const GetServiceOrderVariantIntegrationActionsApiArgSchema = z.object({
  serviceOrderVariantId: z.string(),
});

export type GetServiceOrderVariantIntegrationActionsApiArg = z.infer<
  typeof GetServiceOrderVariantIntegrationActionsApiArgSchema
>;
